/**
 * Context menus when editing and working with proteins.
 */
import * as analytics from 'analytics/Analytics';
import * as asserts from 'Asserts';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as BioUtil from 'biojs/Util';
import * as ContextMenu from 'components/ContextMenu';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import {AaSequenceAnnotationView} from 'molbio/aa-sequences/AaSequenceAnnotationsView';
import {
  ProteinNamesQuery,
  ProteinNamesQueryVariables,
} from 'molbio/aa-sequences/util/__generated/ProteinNamesQuery';
import {ProteinPart} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {
  getAccessibleAaSequenceNamesById,
  AA_SEQUENCE_NAMES_QUERY,
} from 'molbio/aa-sequences/util/AaSequenceQueryUtil';
import PolymerMapTabView from 'molbio/maps/PolymerMapTabView';
import {
  BioentityOpenFeatureStatus,
  getBioentityOpenFeatureStatusesById,
  getOpenFeatureStatusForPartId,
  openBioentityWithFeatureCheck,
} from 'molbio/openBioentityWithFeatureCheck';
import * as sequtil from 'molbio/SeqUtil';
import PolymerMapTabState from 'molbio/tabstates/PolymerMapTabState';
import notifications from 'notifications';
import {BENCHLING_BLAST_MENU_TITLE} from 'search/blast/BLASTConstants';
import {runBLASTModalForSelection} from 'search/blast/BLASTUtil';
import {validateBLASTSequence} from 'search/blast/validation';
import {Operator} from 'search/SearchConstants';
import {deleteAnnotation} from 'toolbar/aa-sequence/AaSequenceAnnotationsPanelUtil';
import {getSelectedAnnotationId, openCreateAnnotation} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import {BasicEvent} from 'util/Types';

export interface AaSequenceClickableContextMenuOption extends ContextMenu.ClickableContextMenuOption {
  hide?: () => boolean | undefined;
  disabled?: () => boolean;
  menu?: Array<AaSequenceContextMenuOption> | null;
}

export type AaSequenceContextMenuOption = AaSequenceClickableContextMenuOption | '-';

function getOpenPartMenuOption(
  partId: string,
  openFeatureStatusesById: Record<string, BioentityOpenFeatureStatus>
): AaSequenceContextMenuOption {
  const openFeatureStatus = getOpenFeatureStatusForPartId(partId, openFeatureStatusesById);
  const isOpenDisabled = !openFeatureStatus.featureStatus.enabled;

  return {
    name: 'Open part',
    click() {
      if (!openFeatureStatus.featureStatus.enabled) {
        return;
      }
      if (openFeatureStatus.itemState != null) {
        FileBrowserActions.openFile(partId, {itemState: openFeatureStatus.itemState});
      } else {
        openBioentityWithFeatureCheck(partId).catch(notifications.notifyError);
      }
    },
    disabled() {
      return isOpenDisabled;
    },
    hide() {
      return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
    },
    tooltip: !openFeatureStatus.featureStatus.enabled
      ? openFeatureStatus.featureStatus.userMessage
      : undefined,
  };
}

function getUnlinkPartMenuOption(aaSequence: AaSequence, part: ProteinPart): AaSequenceContextMenuOption {
  return {
    name: 'Unlink part',
    click() {
      // TODO BNCH-117227
    },
    disabled() {
      return !aaSequence.allowResidueEdits();
    },
    hide() {
      return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
    },
  };
}

// Exported for testing only
export async function getPartMenuOptions(
  aaSequence: AaSequence
): Promise<Array<AaSequenceContextMenuOption>> {
  if (!BenchlingConfig.MOLBIO_ENABLE_AA_PARTS) {
    return [];
  }
  const selection = aaSequence.selection();
  const parts = aaSequence.getPartsInSelection(selection.start(), selection.end());
  if (parts.length === 0) {
    return [];
  } else if (
    selection.isNotEmpty() &&
    !parts.some((part) => sequtil.isRangeWithin(selection.start(), selection.end(), part.start, part.end))
  ) {
    return [];
  } else if (parts.length === 1) {
    const singlePart = parts[0];
    const partHasAnyOverlaps = aaSequence.getPartsInSelection(singlePart.start, singlePart.end).length > 1;
    const openFeatureStatusesById = await getBioentityOpenFeatureStatusesById([singlePart.proteinId]).catch(
      (error) => {
        notifications.notifyError(error);
        return {};
      }
    );
    return [
      getOpenPartMenuOption(singlePart.proteinId, openFeatureStatusesById),
      {
        name: 'Delete part',
        click() {
          AaSequenceActions.deleteAminoAcids(aaSequence.id, {start: singlePart.start, end: singlePart.end});
        },
        disabled() {
          return !aaSequence.allowResidueEdits() || partHasAnyOverlaps;
        },
        hide() {
          return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
        },
      },
      {
        name: 'Replace part',
        click() {
          // TODO BNCH-121772
        },
        disabled() {
          return !aaSequence.allowResidueEdits() || partHasAnyOverlaps;
        },
        hide() {
          return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
        },
      },
      getUnlinkPartMenuOption(aaSequence, singlePart),
      '-',
    ];
  } else {
    const result = await doQuerySafe<ProteinNamesQuery, ProteinNamesQueryVariables>(
      {query: AA_SEQUENCE_NAMES_QUERY},
      {
        variables: {ids: aaSequence.parts().map(({proteinId}) => proteinId)},
      }
    );
    const partNamesById = getAccessibleAaSequenceNamesById(result);
    if (partNamesById == null) {
      asserts.assertAndContinue(false, 'Failed to fetch protein part names for context menu');
      return [];
    }
    const openFeatureStatusesById = await getBioentityOpenFeatureStatusesById(
      parts.map((part) => part.proteinId)
    ).catch((error) => {
      notifications.notifyError(error);
      return {};
    });
    const partsOptions = parts.map((part) => ({
      name: partNamesById[part.proteinId],
      menu: [
        {
          name: 'Select part',
          click() {
            aaSequence.selection().setEndpoints(part.start, part.end);
          },
        },
        getOpenPartMenuOption(part.proteinId, openFeatureStatusesById),
        getUnlinkPartMenuOption(aaSequence, part),
      ],
      hide(): boolean {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    }));
    return [...partsOptions, '-'];
  }
}

export const openContextMenu = async (
  e: BasicEvent | Event,
  aaSequence: AaSequence,
  aaSequenceMapView: PolymerMapTabView<PolymerMapTabState<AaSequence>>
): Promise<void> => {
  const partMenuOptions = await getPartMenuOptions(aaSequence);
  ContextMenu.view.open(e, [
    ...partMenuOptions,
    {
      name: 'Insert residues',
      click() {
        aaSequenceMapView.showInputBubble({});
      },
      disabled() {
        return !aaSequence.allowResidueEdits() || !aaSequence.canInsertAt(aaSequence.selection().start());
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
  ]);
};

export const openSelectionContextMenu = async (
  e: BasicEvent | Event,
  aaSequence: AaSequence
): Promise<void> => {
  // ContextMenu.view.open already stops event propagation, but we have to preemptively call stopPropagation
  // here. This function waits for a Promise to resolve, so the event may propagate before open is called.
  e.stopPropagation();
  const selectedAminoAcids = aaSequence.residueSlice(
    aaSequence.selection().start(),
    aaSequence.selection().end()!
  );
  const hasNonStandardAminoAcids = BioUtil.containsNonStandardAminoAcids(selectedAminoAcids);
  const partMenuOptions = await getPartMenuOptions(aaSequence);
  ContextMenu.view.open(e, [
    ...partMenuOptions,
    {
      name: 'Delete residues',
      click() {
        aaSequence.deleteSelection();
      },
      disabled() {
        return !aaSequence.allowResidueEdits() || !aaSequence.isSelectionDeletable();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    {
      name: 'Create annotation',
      click() {
        analytics.track('protein', 'open_annotation_panel', {
          product: ANALYTICS_MODULE.MOLBIO,
          openedFrom: 'context_menu',
          protein_id: aaSequence.get('id'),
        });
        openCreateAnnotation(aaSequence.id, {
          start: aaSequence.selection().start(),
          end: aaSequence.selection().end(),
        });
        aaSequence.fileState().get('toolbar').trigger('openAaSequenceAnnotationsPanel');
      },
      disabled() {
        return !aaSequence.allowFeatureEdits();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
      dataTestAttrs: {component: 'ContextMenu', element: 'createAnnotation'},
    },
    {
      name: 'Copy',
      copy() {
        return selectedAminoAcids;
      },
    },
    {
      name: hasNonStandardAminoAcids
        ? 'Back translate (cannot back-translate non-standard AAs)'
        : 'Back translate',
      click() {
        aaSequence.handleBackTranslate();
      },
      disabled() {
        return hasNonStandardAminoAcids || aaSequence.isTransientDisplay();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE});
      },
    },
    {
      name: BENCHLING_BLAST_MENU_TITLE,
      async click() {
        await runBLASTModalForSelection(aaSequence.selection());
      },
      disabled() {
        return (
          aaSequence.isTransientDisplay() ||
          aaSequence.selection().isEmpty() ||
          !validateBLASTSequence(aaSequence.selection().getSelectionResidues(), Operator.BLASTP)
        );
      },
      hide() {
        return aaSequence.selection().isEmpty() || !BenchlingConfig.ENABLE_BLAST_SEARCH;
      },
    },
  ]);
};

export const openAnnotationContextMenu = (
  e: BasicEvent | Event,
  aaSequence: AaSequence,
  annotation: AaSequenceAnnotationView
): void => {
  if (aaSequence.isTransientDisplay()) {
    return;
  }
  asserts.assert(
    getSelectedAnnotationId(aaSequence.id) === annotation.id,
    'Expected AA sequence UI state to be up to date before the context menu code runs.'
  );
  ContextMenu.view.open(e, [
    {
      name: 'Delete annotation',
      click() {
        deleteAnnotation(aaSequence.id, aaSequence.getActiveVersionId(), annotation.id, annotation.id);
      },
      disabled() {
        return !aaSequence.allowFeatureEdits() || annotation.isFromPart();
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
      },
    },
    {
      name: 'Add to feature library',
      click() {
        AaSequenceActions.promptAddAnnotationToFeatureLibrary(aaSequence.id, annotation.id);
      },
      hide() {
        return !isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN});
      },
    },
  ]);
};
